/**
 * 来場者新規登録画面
 * ユーザ名・パスワード・パスワード確認を入力し、利用規約に同意して新規アカウントを作成します
 * 上から 切替 → 入力欄 → 規約同意 → 登録ボタン → 文字の大きさ の順に並べ、エラーは該当する欄の直下に出します
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, Text, TextInput, View } from '../../../shared/components/WebPrimitives';
import {
  REGISTER_BUTTON_LABEL,
  REGISTER_BUTTON_LOADING_LABEL,
  VISITOR_REGISTER_GO_TO_LOGIN_LABEL,
  VISITOR_REGISTER_RETURN_TO_QR_GUIDANCE_MESSAGE,
  VISITOR_REGISTER_USERNAME_TAKEN_LOGIN_GUIDE_MESSAGE,
  VISITOR_TERMS_VERSION_DISPLAY_LABEL,
} from '../constants';
import VisitorAuthModeSwitch from '../components/VisitorAuthModeSwitch';
import VisitorTermsModal, {
  formatTermsEffectiveDate,
} from '../components/VisitorTermsModal';
import {
  useVisitorRegisterForm,
  type RegisterErrorField,
  type RegisterFormError,
  type RegisterInvalidField,
} from '../hooks/useVisitorRegisterForm';
import { resolveLoginReturnDestination } from '../utils/loginReturnDestination';
import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import AppButton from '../../../shared/components/AppButton';
import PasswordVisibilityToggleButton from '../../../shared/components/PasswordVisibilityToggleButton';
import ScreenLayout from '../../../shared/components/ScreenLayout';
import StorageUnavailableNotice from '../../../shared/components/StorageUnavailableNotice';
import TextSizeControl from '../../../shared/components/TextSizeControl';
import usePublicAppSettings from '../../../shared/hooks/usePublicAppSettings';
import {
  VISITOR_PASSWORD_MAX_LENGTH,
  VISITOR_USERNAME_MAX_LENGTH,
  countVisitorUsernameCharacters,
} from '../../../shared/utils/validation';
import { INPUT_PLACEHOLDER_COLOR } from '../../../shared/constants/accessibility';

/**
 * エラー表示要素のIDを返す（入力欄の`aria-describedby`から参照する）
 * @param {RegisterErrorField} field - エラーの表示場所
 * @returns {string} エラー表示要素のID
 */
const getRegisterErrorId = (field: RegisterErrorField): string => `visitor-register-${field}-error`;

/**
 * 指定した欄のエラーを、その欄の直下に表示する
 * @param {Object} props - プロパティ
 * @param {RegisterErrorField} props.field - 表示する欄
 * @param {RegisterFormError | null} props.formError - 現在のエラー
 * @returns {React.JSX.Element | null} エラー表示（この欄のエラーでなければ何も出さない）
 */
const RegisterFieldError = ({
  field,
  formError,
}: {
  field: RegisterErrorField;
  formError: RegisterFormError | null;
}): React.JSX.Element | null => {
  if (formError?.field !== field) {
    return null;
  }

  return (
    <Text
      accessibilityLiveRegion="assertive"
      accessibilityRole="alert"
      className="mt-2 text-sm font-medium text-app-danger"
      id={getRegisterErrorId(field)}
    >
      {formError.message}
    </Text>
  );
};

/**
 * 来場者新規登録画面
 * @param {Object} props - プロパティ
 * @param {Object} props.navigation - ナビゲーション
 * @param {Object} [props.route] - ルート（QRで開いた画面から来た場合は戻り先を持つ）
 * @returns {JSX.Element} 来場者新規登録画面
 */
const VisitorRegisterScreen = ({ navigation, route }) => {
  /** 公開アプリ設定 */
  const { settings } = usePublicAppSettings();

  /** 登録後に戻るQRの画面 */
  const returnDestination = useMemo(
    () => resolveLoginReturnDestination(route?.params?.returnTo),
    [route?.params?.returnTo],
  );

  /** 新規登録フォーム状態 */
  const {
    username,
    password,
    passwordConfirmation,
    terms,
    isTermsLoading,
    termsErrorMessage,
    isTermsAccepted,
    isTermsModalVisible,
    formError,
    isSubmitting,
    isSubmitDisabled,
    handleUsernameChange,
    handlePasswordChange,
    handlePasswordConfirmationChange,
    handleToggleTermsAccepted,
    handleOpenTermsModal,
    handleCloseTermsModal,
    handleRetryTerms,
    submitRegister,
  } = useVisitorRegisterForm();

  /** ユーザ名入力要素 */
  const usernameInputRef = useRef<HTMLInputElement>(null);

  /** パスワード入力要素 */
  const passwordInputRef = useRef<HTMLInputElement>(null);

  /** 確認用パスワード入力要素 */
  const passwordConfirmationInputRef = useRef<HTMLInputElement>(null);

  /** 利用規約の同意チェック要素 */
  const termsCheckboxRef = useRef<HTMLButtonElement>(null);

  /** パスワード・確認用パスワードを画面表示中か（既定は非表示、1ボタンで両欄を同時に切り替える） */
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  /**
   * パスワード・確認用パスワードの表示・非表示を同時に切り替える
   * @returns {void}
   */
  const handleTogglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible((previous) => !previous);
  }, []);

  /**
   * 最初の不正入力（規約未同意なら同意チェック）へDOM更新後にfocusする
   * focusでブラウザが要素を画面内へスクロールするため、下にある同意チェックも見える位置に来る
   * @param {RegisterInvalidField | undefined} invalidField - 不正入力名
   * @returns {void}
   */
  const focusInvalidInput = useCallback((invalidField: RegisterInvalidField | undefined) => {
    if (!invalidField) {
      return;
    }

    /** focus対象の要素ref */
    const targetRef = {
      username: usernameInputRef,
      password: passwordInputRef,
      passwordConfirmation: passwordConfirmationInputRef,
      terms: termsCheckboxRef,
    }[invalidField];

    window.requestAnimationFrame(() => {
      targetRef.current?.focus();
    });
  }, []);

  /**
   * 指定した欄にエラーがあるときの`aria-invalid`・`aria-describedby`を返す
   * @param {RegisterInvalidField} field - 欄
   * @returns {{ 'aria-invalid': boolean, 'aria-describedby'?: string }} 欄に付けるaria属性
   */
  const getFieldErrorAriaProps = (field: RegisterInvalidField) => {
    /** この欄のエラーか */
    const hasError = formError?.field === field;

    return {
      'aria-invalid': hasError,
      'aria-describedby': hasError ? getRegisterErrorId(field) : undefined,
    };
  };

  /** 入力値で来場者ホームへ遷移する */
  const handlePressRegister = useCallback(async () => {
    /** 登録送信結果 */
    const submitResult = await submitRegister();

    if (!submitResult.isSuccess || !submitResult.payload) {
      focusInvalidInput(submitResult.invalidField);
      return;
    }

    // セッション情報はlocalStorageに保存済みのため、URLへ秘密情報を渡さない
    if (returnDestination) {
      // QRの続きへ戻り、戻る操作で登録画面を開き直さないよう履歴を置き換える
      navigation.replace(returnDestination.name, returnDestination.params);
      return;
    }

    navigation.navigate(VISITOR_ROUTES.HOME);
  }, [focusInvalidInput, navigation, returnDestination, submitRegister]);

  /** ログイン画面へ遷移する（QRの戻り先は持ったまま） */
  const handlePressGoToLogin = useCallback(() => {
    if (returnDestination) {
      navigation.navigate(VISITOR_ROUTES.LOGIN, { returnTo: returnDestination });
      return;
    }

    navigation.navigate(VISITOR_ROUTES.LOGIN);
  }, [navigation, returnDestination]);

  /** ユーザ名入力文字数 */
  const usernameLength = countVisitorUsernameCharacters(username.trim());

  return (
    <ScreenLayout
      badge="新たな冒険者へ"
      description="ユーザ名とパスワードを決めて、新しい冒険を始めましょう。"
      mascotMood="happy"
      mascotPose="guide"
      theme="visitor"
      title="来場者新規登録"
    >
      <ScrollView
        className="flex-1"
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        testID="visitor-register-scroll-view"
      >
        <View className="gap-4 pb-8">
        <VisitorAuthModeSwitch current="register" onSwitch={handlePressGoToLogin} />

        <StorageUnavailableNotice />

        {returnDestination ? (
          <View className="border-2 border-app-quest-night/20 bg-[#FFF8DD] p-3" testID="visitor-register-return-to-qr-guidance">
            <Text className="text-sm leading-5 text-app-text">
              {VISITOR_REGISTER_RETURN_TO_QR_GUIDANCE_MESSAGE}
            </Text>
          </View>
        ) : null}

        <View>
          <Text className="mb-2 text-sm font-medium text-app-text">ユーザ名</Text>
          <TextInput
            accessibilityLabel="ユーザ名"
            {...getFieldErrorAriaProps('username')}
            autoCapitalize="none"
            autoComplete="username"
            autoCorrect={false}
            className="rounded-none border-2 border-app-border bg-[#FFFDF0] px-4 py-4 text-base text-app-text"
            maxLength={VISITOR_USERNAME_MAX_LENGTH}
            onChangeText={handleUsernameChange}
            placeholder={settings.visitorUsernamePlaceholder}
            placeholderTextColor={INPUT_PLACEHOLDER_COLOR}
            ref={usernameInputRef}
            value={username}
          />
          <RegisterFieldError field="username" formError={formError} />
          {formError?.isUsernameTaken ? (
            // 以前に登録した人が別名で作り直すとptが分かれるため、重複時だけログインへの道を示す
            <View className="mt-2 gap-1 border-2 border-app-quest-night/20 bg-[#FFF8DD] p-3">
              <Text className="text-sm leading-5 text-app-text">
                {VISITOR_REGISTER_USERNAME_TAKEN_LOGIN_GUIDE_MESSAGE}
              </Text>
              <Pressable
                accessibilityRole="button"
                className="min-h-[44px] justify-center self-start px-1"
                onPress={handlePressGoToLogin}
                testID="visitor-register-go-to-login-button"
              >
                <Text className="text-sm font-bold text-app-quest-night underline">
                  {VISITOR_REGISTER_GO_TO_LOGIN_LABEL}
                </Text>
              </Pressable>
            </View>
          ) : null}
          <Text className="mt-2 text-right text-xs text-app-text-secondary">
            {usernameLength}/{VISITOR_USERNAME_MAX_LENGTH}
          </Text>
          <Text className="mt-2 text-xs leading-5 text-app-text-secondary">
            ユーザ名はptランキングや友達グループで他の人にも表示されます。本名や連絡先は使わないでください。
          </Text>
        </View>

        <View>
          <Text className="mb-2 text-sm font-medium text-app-text">パスワード</Text>
          <View className="flex-row items-center gap-2">
            <TextInput
              accessibilityLabel="パスワード"
              {...getFieldErrorAriaProps('password')}
              autoCapitalize="none"
              autoComplete="new-password"
              autoCorrect={false}
              className="min-w-0 flex-1 rounded-none border-2 border-app-border bg-[#FFFDF0] px-4 py-4 text-base text-app-text"
              maxLength={VISITOR_PASSWORD_MAX_LENGTH}
              onChangeText={handlePasswordChange}
              placeholder="4文字以上"
              placeholderTextColor={INPUT_PLACEHOLDER_COLOR}
              ref={passwordInputRef}
              secureTextEntry={!isPasswordVisible}
              value={password}
            />
            <PasswordVisibilityToggleButton
              isVisible={isPasswordVisible}
              onToggle={handleTogglePasswordVisibility}
              testID="visitor-register-password-visibility-toggle"
            />
          </View>
          <RegisterFieldError field="password" formError={formError} />
          <Text className="mt-2 text-xs leading-5 text-app-text-secondary">
            4文字以上。ユーザ名とパスワードは再ログインで使います。忘れた場合は{settings.supportContactLabel}へ相談してください。
          </Text>
        </View>

        <View>
          <Text className="mb-2 text-sm font-medium text-app-text">パスワード（確認）</Text>
          <View className="flex-row items-center gap-2">
            <TextInput
              accessibilityLabel="パスワード（確認）"
              {...getFieldErrorAriaProps('passwordConfirmation')}
              autoCapitalize="none"
              autoComplete="new-password"
              autoCorrect={false}
              className="min-w-0 flex-1 rounded-none border-2 border-app-border bg-[#FFFDF0] px-4 py-4 text-base text-app-text"
              maxLength={VISITOR_PASSWORD_MAX_LENGTH}
              onChangeText={handlePasswordConfirmationChange}
              placeholder="同じパスワードをもう一度"
              placeholderTextColor={INPUT_PLACEHOLDER_COLOR}
              ref={passwordConfirmationInputRef}
              secureTextEntry={!isPasswordVisible}
              value={passwordConfirmation}
            />
          </View>
          <RegisterFieldError field="passwordConfirmation" formError={formError} />
        </View>

        <View className="gap-3 border-2 border-app-quest-night/20 bg-[#FFF8DD] p-4">
          {isTermsLoading ? (
            <Text className="text-sm text-app-text-secondary">利用規約を確認しています...</Text>
          ) : null}

          {!isTermsLoading && terms ? (
            <>
              <View>
                <Text className="text-xs text-app-text-secondary">
                  {terms.title}（{VISITOR_TERMS_VERSION_DISPLAY_LABEL} / 更新日: {formatTermsEffectiveDate(terms.effectiveAt)}）
                </Text>
                <Pressable
                  accessibilityRole="button"
                  className="min-h-[44px] justify-center self-start px-1"
                  onPress={handleOpenTermsModal}
                  testID="open-visitor-terms-button"
                >
                  <Text className="text-sm font-bold text-app-quest-night underline">利用規約を読む</Text>
                </Pressable>
                <Text className="text-xs leading-5 text-app-danger">
                  未成年者は法定代理人の同意を得たうえで同意してください。
                </Text>
              </View>

              <View>
                <Pressable
                  accessibilityLabel="利用規約に同意します"
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: isTermsAccepted }}
                  aria-checked={isTermsAccepted}
                  {...getFieldErrorAriaProps('terms')}
                  className="min-h-[48px] flex-row items-center gap-3 border-2 border-app-quest-night/30 bg-[#FFFDF0] px-3 py-2"
                  onPress={handleToggleTermsAccepted}
                  ref={termsCheckboxRef}
                  testID="visitor-terms-checkbox"
                >
                  <View
                    className={`h-6 w-6 items-center justify-center border-2 border-app-quest-night ${
                      isTermsAccepted ? 'bg-app-quest-night' : 'bg-white'
                    }`}
                  >
                    <Text className="text-sm font-bold text-white">{isTermsAccepted ? '✓' : ''}</Text>
                  </View>
                  <Text className="flex-1 text-left text-sm font-semibold text-app-text">
                    利用規約に同意します
                  </Text>
                </Pressable>
                <RegisterFieldError field="terms" formError={formError} />
              </View>
            </>
          ) : null}

          {!isTermsLoading && termsErrorMessage ? (
            <>
              <Text className="text-sm font-medium text-app-danger">{termsErrorMessage}</Text>
              <Pressable
                accessibilityRole="button"
                className="min-h-[44px] justify-center border-2 border-app-quest-night bg-[#EAF3FF] px-4"
                onPress={handleRetryTerms}
              >
                <Text className="text-center text-sm font-bold text-app-quest-night">利用規約を再取得</Text>
              </Pressable>
            </>
          ) : null}
        </View>

        <RegisterFieldError field="form" formError={formError} />

        <AppButton
          disabled={isSubmitDisabled}
          label={isSubmitting ? REGISTER_BUTTON_LOADING_LABEL : REGISTER_BUTTON_LABEL}
          onPress={handlePressRegister}
        />

        <TextSizeControl />

        </View>

        <VisitorTermsModal
          onClose={handleCloseTermsModal}
          terms={terms}
          visible={isTermsModalVisible}
        />
      </ScrollView>
    </ScreenLayout>
  );
};

export default VisitorRegisterScreen;
